/**
 * /admin/users/erase — GDPR erasure form (Story D2).
 *
 * Admin form: target user ID, confirmation_email input (must type exact
 * email to enable the danger button), reason textarea, danger-styled
 * button. On submit, calls POST /api/admin/users/:id/erase.
 */

import { type ReactElement, useState } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Input } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

type FetchState = 'idle' | 'loading' | 'success' | 'error';

export function AdminUserErasePage(): ReactElement {
  const [userId, setUserId] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<FetchState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState<{ user_id: string; audit_log_id: string } | null>(null);

  const canSubmit =
    userId.trim().length > 0 &&
    confirmationEmail.trim().length > 0 &&
    reason.trim().length > 0 &&
    state !== 'loading';

  async function handleSubmit(): Promise<void> {
    setState('loading');
    setErrorMsg('');
    setResult(null);
    try {
      const res = await apiFetch(`/api/admin/users/${userId.trim()}/erase`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          confirmation_email: confirmationEmail.trim(),
          reason: reason.trim(),
        }),
      });
      const body = (await res.json()) as {
        ok: boolean;
        data?: { user_id: string; audit_log_id: string };
        error?: { code?: string; message?: string; field?: string };
      };
      if (!body.ok) {
        setErrorMsg(
          body.error?.code === 'confirmation_mismatch'
            ? 'Confirmation email does not match the user\'s registered email.'
            : body.error?.message ?? 'Erasure failed',
        );
        setState('error');
        return;
      }
      setResult(body.data ?? null);
      setState('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Network error');
      setState('error');
    }
  }

  return (
    <AdminLayout title="GDPR Erasure" eyebrow="Admin / Users">
      <Card variant="default" className="mt-6 max-w-lg">
        <p className="mb-5 rounded-md border border-line-2 bg-bg-warm px-4 py-3 font-sans text-small text-ink-2">
          ⚠️ This action permanently anonymises the user's data. This cannot be undone.
        </p>

        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <div>
            <label
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
              htmlFor="erase-user-id"
            >
              User ID
            </label>
            <Input
              id="erase-user-id"
              type="text"
              placeholder="Paste user UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>

          <div>
            <label
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
              htmlFor="erase-confirmation-email"
            >
              Confirmation Email
            </label>
            <Input
              id="erase-confirmation-email"
              type="email"
              placeholder="Type the user's email to confirm"
              value={confirmationEmail}
              onChange={(e) => setConfirmationEmail(e.target.value)}
            />
          </div>

          <div>
            <label
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
              htmlFor="erase-reason"
            >
              Reason
            </label>
            <textarea
              id="erase-reason"
              placeholder="GDPR erasure request reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            />
          </div>

          <div>
            <Button
              variant="danger"
              type="submit"
              disabled={!canSubmit}
            >
              {state === 'loading' ? 'Processing erasure…' : 'Erase user data'}
            </Button>
          </div>
        </form>

        {state === 'success' && result && (
          <div className="mt-5 rounded-md border border-line-2 bg-bg-warm px-4 py-3">
            <p className="font-sans text-small font-medium text-ink">
              User data erased successfully.
            </p>
            <p className="mt-1 font-mono text-mono text-ink-2">User ID: {result.user_id}</p>
            <p className="mt-1 font-mono text-mono text-ink-2">
              Audit Log ID: {result.audit_log_id}
            </p>
          </div>
        )}

        {state === 'error' && errorMsg && (
          <p role="alert" className="mt-4 font-sans text-small text-error">
            {errorMsg}
          </p>
        )}
      </Card>
    </AdminLayout>
  );
}
